import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

// In production (Vercel), set REACT_APP_API_URL to the Railway backend URL.
// In development the proxy in package.json handles it, so this is empty.
axios.defaults.baseURL = process.env.REACT_APP_API_URL || "";

const AuthContext = createContext(null);

const TOKEN_KEY = "routemuse_token";

function tokenIsValid(token) {
  try {
    const { exp } = jwtDecode(token);
    return exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // { email, username, token, credits }
  const [loading, setLoading] = useState(true);

  // Restore session from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && tokenIsValid(token)) {
      const decoded = jwtDecode(token);
      // WP JWT puts email under user_email, not email — normalise it
      const email = decoded.user_email || decoded.email || "";
      // Fetch fresh credits then finish loading
      axios
        .get("/api/auth/me", { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setUser({ ...decoded, email: res.data.email || email, token, credits: res.data.credits }))
        .catch(() => {
          localStorage.removeItem(TOKEN_KEY);
        })
        .finally(() => setLoading(false));
    } else {
      localStorage.removeItem(TOKEN_KEY);
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await axios.post("/api/auth/login", { email, password });
    const { token, username, credits } = res.data;
    const decoded = JSON.parse(atob(token.split(".")[1]));
    const userEmail = decoded.user_email || decoded.email || email;
    localStorage.setItem(TOKEN_KEY, token);
    setUser({ email: userEmail, username, token, credits });
    return res.data;
  }, []);

  const register = useCallback(async (email, password, username) => {
    const res = await axios.post("/api/auth/register", { email, password, username });
    const { token, username: displayName, credits } = res.data;
    const decoded = JSON.parse(atob(token.split(".")[1]));
    const userEmail = decoded.user_email || decoded.email || email;
    localStorage.setItem(TOKEN_KEY, token);
    setUser({ email: userEmail, username: displayName || username, token, credits });
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const refreshCredits = useCallback(async () => {
    setUser((prev) => {
      if (!prev) return prev;
      axios
        .get("/api/auth/me", { headers: { Authorization: `Bearer ${prev.token}` } })
        .then((res) => setUser((u) => u ? { ...u, credits: res.data.credits } : u))
        .catch(() => {});
      return prev;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, login, logout, register, refreshCredits, isLoggedIn: !!user, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
